import { Link } from 'react-router-dom';
import { SeoPage, CtaInline } from './SeoLayout';
import { BUSINESS, getLocalBusinessSchema, getBreadcrumbSchema } from '../../data/businessInfo';
import { CheckCircle2, MapPin, Phone, Clock, Star, ArrowRight } from 'lucide-react';
import { SERVICES } from './LeistungenOverview';

export default function MalerDormagenHub() {
  const path = '/maler-dormagen';
  const title = 'Maler Dormagen | FarbWelt Maler Express Service — Handwerksbetrieb aus Dormagen';
  const desc = 'Ihr Malerbetrieb aus Dormagen: Wandanstrich, Tapezieren, Fassade, Renovierung, Wohnungsübergabe. Kostenlose Besichtigung, transparenter Festpreis, schnelle Termine.';

  return (
    <SeoPage
      title={title}
      description={desc}
      canonicalPath={path}
      kicker="Maler Dormagen · Hauptseite"
      heroTitle="Ihr Malerbetrieb aus Dormagen"
      heroSubtitle="FarbWelt Maler Express Service — seit vielen Aufträgen im Dienst von Privatkunden, Vermietern und Gewerbe in Dormagen, Neuss, Köln, Düsseldorf und Umgebung."
      jsonLd={[
        getLocalBusinessSchema(),
        getBreadcrumbSchema([
          { name: 'Startseite', item: '/' },
          { name: 'Maler Dormagen', item: path }
        ]),
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${BUSINESS.domain}${path}`,
          url: `${BUSINESS.domain}${path}`,
          name: title,
          description: desc,
          isPartOf: { '@type': 'WebSite', url: BUSINESS.domain, name: BUSINESS.name },
          about: { '@id': `${BUSINESS.domain}/#organization` },
          inLanguage: 'de-DE'
        }
      ]}
    >
      <div className="not-prose grid sm:grid-cols-3 gap-4 my-6">
        <div className="p-4 bg-[#faf7f5] rounded-lg text-center">
          <MapPin className="w-6 h-6 text-[#9B1B30] mx-auto mb-2" />
          <div className="text-xs text-gray-500 uppercase">Aus Dormagen</div>
          <div className="font-bold text-[#1A1A1A]">Für Dormagen</div>
        </div>
        <div className="p-4 bg-[#faf7f5] rounded-lg text-center">
          <Clock className="w-6 h-6 text-[#9B1B30] mx-auto mb-2" />
          <div className="text-xs text-gray-500 uppercase">Reaktionszeit</div>
          <div className="font-bold text-[#1A1A1A]">Meist am selben Tag</div>
        </div>
        <div className="p-4 bg-[#faf7f5] rounded-lg text-center">
          <Star className="w-6 h-6 text-[#9B1B30] mx-auto mb-2" />
          <div className="text-xs text-gray-500 uppercase">Festpreis</div>
          <div className="font-bold text-[#1A1A1A]">Transparent</div>
        </div>
      </div>

      <h2>Wer wir sind</h2>
      <p>
        FarbWelt Maler Express Service ist ein Handwerks­betrieb mit Sitz in Dormagen. Wir sind kein
        Groß­konzern und kein Franchise — Sie sprechen bei uns direkt mit den Leuten, die auch bei
        Ihnen arbeiten. Eingetragen bei der {BUSINESS.chamber} (USt-IdNr. {BUSINESS.vatId}).
      </p>

      <h2>Was wir in Dormagen und Umgebung machen</h2>
      <p>Sechs Kern­leistungen — als Komplett­auftrag oder einzeln:</p>

      <div className="not-prose grid sm:grid-cols-2 gap-3 my-6">
        {SERVICES.map(s => (
          <Link
            key={s.slug}
            to={`/leistungen/${s.slug}`}
            className="group flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:border-[#9B1B30] hover:shadow-md transition-all"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#9B1B30]/10 flex items-center justify-center">
              <s.icon className="w-5 h-5 text-[#9B1B30]" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-[#1A1A1A] group-hover:text-[#9B1B30] transition-colors">
                {s.title}
              </div>
              <div className="text-sm text-gray-600 mb-1">{s.short}</div>
              <span className="text-xs text-[#9B1B30] font-semibold inline-flex items-center gap-1">
                Details <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      <CtaInline text="Kostenlose Besichtigung in Dormagen — meist innerhalb weniger Tage" />

      <h2>Warum Kunden aus Dormagen uns wählen</h2>
      <ul className="not-prose grid sm:grid-cols-2 gap-3 my-4">
        <li className="flex items-start gap-3 p-3">
          <CheckCircle2 className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-0.5" />
          <div>
            <strong>Festpreis vor Auftrag</strong><br />
            <span className="text-sm text-gray-600">Kein Klein-Gedrucktes, keine Nach­verhandlungen bei der Rechnung.</span>
          </div>
        </li>
        <li className="flex items-start gap-3 p-3">
          <CheckCircle2 className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-0.5" />
          <div>
            <strong>Kurze Anfahrt</strong><br />
            <span className="text-sm text-gray-600">Aus Dormagen kein Fahrt­kosten­aufschlag für Dormagen.</span>
          </div>
        </li>
        <li className="flex items-start gap-3 p-3">
          <CheckCircle2 className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-0.5" />
          <div>
            <strong>Direkte Ansprechpartner</strong><br />
            <span className="text-sm text-gray-600">Kein Callcenter — wer bei Ihnen arbeitet, spricht auch mit Ihnen.</span>
          </div>
        </li>
        <li className="flex items-start gap-3 p-3">
          <CheckCircle2 className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-0.5" />
          <div>
            <strong>Saubere Baustelle</strong><br />
            <span className="text-sm text-gray-600">Wir decken sorg­fältig ab und räumen täglich auf.</span>
          </div>
        </li>
        <li className="flex items-start gap-3 p-3">
          <CheckCircle2 className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-0.5" />
          <div>
            <strong>Steuerlich absetzbar</strong><br />
            <span className="text-sm text-gray-600">Arbeits­leistung auf Rechnung separat aus­gewiesen (§ 35a EStG).</span>
          </div>
        </li>
        <li className="flex items-start gap-3 p-3">
          <CheckCircle2 className="w-5 h-5 text-[#9B1B30] flex-shrink-0 mt-0.5" />
          <div>
            <strong>Werktags erreichbar</strong><br />
            <span className="text-sm text-gray-600">Antwort meist am selben Tag, keine Warteschleife.</span>
          </div>
        </li>
      </ul>

      <h2>Wie Sie uns beauftragen</h2>
      <ol>
        <li>Anruf unter <a href={`tel:${BUSINESS.phone}`}><strong>{BUSINESS.phoneDisplay}</strong></a> oder{' '}
          <Link to="/termin">Online-Termin buchen</Link>.</li>
        <li>Kostenlose Besichtigung vor Ort — meist innerhalb weniger Tage.</li>
        <li>Sie bekommen ein schriftliches Festpreis-Angebot.</li>
        <li>Nach Auftrags­bestätigung: Terminvereinbarung und Ausführung.</li>
        <li>Übergabe, Rechnung, fertig.</li>
      </ol>
      <p>Ausführlich unter <Link to="/so-arbeiten-wir">So arbeiten wir →</Link></p>

      <h2>Häufige Fragen aus Dormagen</h2>
      <p>
        Was kostet ein Zimmer streichen? Wie schnell können Sie kommen? Muss ich vor Auszug wirklich
        streichen? Alle Antworten finden Sie zusammengefasst in unserer <Link to="/faq">FAQ →</Link>
      </p>

      <h2>Ratgeber für Ihr Projekt</h2>
      <p>
        Wenn Sie noch überlegen, ob Sie selbst anpacken oder einen Betrieb beauftragen:{' '}
        <Link to="/ratgeber/wohnungsuebergabe">Wohnungs­übergabe</Link>,{' '}
        <Link to="/ratgeber/tapete-entfernen">Tapete entfernen</Link>,{' '}
        <Link to="/ratgeber/wandfarbe-auswaehlen">Wandfarbe auswählen</Link> — ehrliche Anleitungen
        vom Fach­mann.
      </p>

      <h2>Kontakt auf einen Blick</h2>
      <div className="not-prose grid sm:grid-cols-2 gap-4">
        <a href={`tel:${BUSINESS.phone}`} className="flex items-center gap-3 p-4 bg-[#faf7f5] rounded-lg hover:bg-[#f0e8e5] transition-colors">
          <Phone className="w-5 h-5 text-[#9B1B30]" />
          <div>
            <div className="text-xs text-gray-500 uppercase">Telefon</div>
            <div className="font-bold text-[#1A1A1A]">{BUSINESS.phoneDisplay}</div>
          </div>
        </a>
        <div className="flex items-center gap-3 p-4 bg-[#faf7f5] rounded-lg">
          <MapPin className="w-5 h-5 text-[#9B1B30]" />
          <div>
            <div className="text-xs text-gray-500 uppercase">Sitz</div>
            <div className="font-bold text-[#1A1A1A] text-sm">{BUSINESS.street}, {BUSINESS.postalCode} {BUSINESS.city}</div>
          </div>
        </div>
      </div>
    </SeoPage>
  );
}
